import { Component, HostListener, ChangeDetectorRef, OnDestroy, signal, OnInit } from '@angular/core';
import { ThemeService } from '../../services/theme/theme.service';
import { PopupService } from '../../services/popup/popup.service';
import { LoginComponent } from '../../../features/login/login.component';
import { AuthService } from '../../services/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { SessionPopupComponent } from '../../../shared/components/session-popup/session-popup.component';
import { gigyaInfo } from '../../../types/gigyaInfo';
import { Router } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { MessagePopupComponent } from '../../../shared/components/message-popup/message-popup.component';
import { NotificationMessagePopupComponent } from '../../../shared/components/notification-message-popup/notification-message-popup.component';
import { HappiService } from 'src/app/shared/services/happi/happi.service';
import { environment } from 'src/environments/environment';
import { CacheService } from 'src/app/shared/services/cache/cache.service';
import { HeaderService } from '../../services/header/header.service';
import { OFFLINE_POPUP_SHOWN_KEY, SELECTED_LANGUAGE_KEY } from 'src/app/types/constants';
import { MAT_MENU_DEFAULT_OPTIONS } from '@angular/material/menu';

declare function showOrHideMenu(): void;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.sass'],
    standalone: false,
    providers: [{
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: {
        overlayPanelClass: ['user-menu-panel'],
      },
    }]
})

export class HeaderComponent implements OnDestroy, OnInit {

  isMobileMenuOpen = false;
  isUserSubMenuOpen = false;
  isUserMenuOpen = false;
  userDetails: any = {};
  userAccessCheck = false;
  menuItemIcons: string[] = [];
  menuItemLabels: string[] = [];
  routeLinks: string[] = [];
  private idleSubscriptions: any[] = [];

  toggleAccessibilityMenu(): void {
    if(typeof showOrHideMenu === 'function') {
      showOrHideMenu();
    } else {
      if(!environment.production) {
        console.error('Accessibility function showOrHideMenu() not found.');
      }
    }
  }

  showHappiIcon$ = this.happiService.isHappiAvailable$;

  constructor(private themeService: ThemeService, private popupService: PopupService,
              private translate: TranslateService, private authService: AuthService, 
              private router: Router, private idle: Idle, private keepalive: Keepalive,
              private cdr: ChangeDetectorRef, private happiService: HappiService,
              private cacheService: CacheService, private headerService: HeaderService) {

    this.authService.gigyaUser$.subscribe((userDetails: gigyaInfo | null) => {
      this.userDetails = userDetails;
      if(userDetails && Object.keys(userDetails).length > 0 && this.isAuthenticated()) {
        if((this.userDetails.accountId === 1 || this.userDetails.accountId === 3) && this.userDetails.accessIdentifier === 'INDADMIN') {
          this.userAccessCheck = true;
          this.menuItemIcons = ['user', 'calendar', 'sheet-warranty', 'parameters'];
          this.menuItemLabels = ['HEADER_LIST_EDIT_PROFILE', 'HEADER_LIST_SUBSCRIPTIONS', 'HEADER_LIST_INVOICE', 'HEADER_LIST_ADMINISTRATION'];
          this.routeLinks = ['/edit-profile', '/subscriptions', '/invoices', '/administration'];
        } else if(this.userDetails.accessIdentifier === 'ORGADMIN') {
          this.userAccessCheck = false;          
          this.menuItemIcons = ['calendar', 'parameters'];
          this.menuItemLabels = ['HEADER_LIST_SUBSCRIPTIONS', 'HEADER_LIST_ADMINISTRATION'];
          this.routeLinks = ['/subscriptions', '/administration'];
        } else if(this.userDetails.accessIdentifier === 'ORGUSER') {
          this.userAccessCheck = false;
          this.menuItemIcons = ['parameters'];
          this.menuItemLabels = ['HEADER_LIST_ADMINISTRATION'];
          this.routeLinks = ['/administration'];
        }
        // Clear any existing idle subscriptions to avoid duplicates
        this.idleSubscriptions.forEach(sub => sub.unsubscribe());
        this.idleSubscriptions = [];

        this.idle.setIdle(300); // Idle time is set to 5 minutes
        this.idle.setTimeout(300); // Respond time before getting timed out - 5 minutes
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // Interurpts can be made through DOM events or mouse and keyboard events
        this.idleSubscriptions.push(
          this.idle.onTimeout.subscribe(() => this.logout())
        );

        this.idleSubscriptions.push(
          this.idle.onIdleStart.subscribe(() => {
            let dialogRef = this.popupService.openDialog(SessionPopupComponent, {
              data: {
                message: this.translate.instant('MSG_SESSION_TIMEDOUT')
              }
            });
            dialogRef.afterClosed().subscribe((confirmed: string) => {
              if(confirmed === 'continue') {
                this.idle.watch();
              }
              if(confirmed === 'close') {
                this.logout();
              }
            });
          })
        );
        this.idleSubscriptions.push(
          this.idle.onTimeoutWarning.subscribe((countdown: any) => {
            if(!environment.production) {
              console.log(`IDLE: will logout in ${countdown} sec`);
            }
          })
        );
        this.keepalive.interval(15);
        this.idle.watch();
        this.cdr.detectChanges();
      } else {
        // User logged out or not authenticated - stop idle monitoring
        this.idleSubscriptions.forEach(sub => sub.unsubscribe());
        this.idleSubscriptions = [];
        this.idle.stop();
        this.menuItemIcons = [];
        this.menuItemLabels = [];
        this.routeLinks = [];
        this.cdr.detectChanges();
      }
    });

  }

  ngOnInit() {
    this.pollStorageForLanguage(3);
    if(this.canShowOfflinePopup()) {
      this.openOfflinePopup();
    }
  }

  // The payment provider redirects back with a full page reload, which re-runs ngOnInit.
  private canShowOfflinePopup(): boolean {
    if(this.router.url.startsWith('/paymentResponse') || this.router.url.startsWith('/payment')) {
      return false;
    }
    return this.cacheService.getItem(OFFLINE_POPUP_SHOWN_KEY) !== true;
  }

  /**
   * Changes the current language based on the selected index.
   * @param index - The index of the language to switch to.
   */
  getThemeMode(): string {
    return this.themeService.getThemeMode();
  }

  /**
   * Opens the login component in a popup dialog.
   * 
   */
  openLoginPopup() {
      this.popupService.openDialog(LoginComponent);
  }

  openOfflinePopup(){
    let selectedLanguage = Object.keys(this.cacheService.getItem(SELECTED_LANGUAGE_KEY)).length > 0 ? this.cacheService.getItem(SELECTED_LANGUAGE_KEY) : this.translate.currentLang;
    const request = { type: 'asos', displayMode: 'offline' };
    this.cacheService.setItem(OFFLINE_POPUP_SHOWN_KEY, true);
    this.headerService.getLoginPopupMessage(request, selectedLanguage).subscribe({
      next: (apiMessage: any) => {
        const message = apiMessage;
        if(message && message.trim() !== '') {
          this.popupService.openDialog(NotificationMessagePopupComponent, {
          data: { message, isHtml: true}
        });
        }else {
          console.log('No offline notification message received');
        }
      },
      error: () => {
        console.log('Error getting offline notification message');
      }
    });
  }

  /**
   * Toggles the mobile menu visibility.
   */
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if(!this.isMobileMenuOpen) {
      this.isUserSubMenuOpen = false;
    }
  }

  /**
   * Closes the mobile menu.
   */
  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.isUserSubMenuOpen = false;
  }

  toggleUserSubMenu() {
    this.isUserSubMenuOpen = !this.isUserSubMenuOpen;
  }

  logout() {
    this.isUserMenuOpen = false;
    this.authService.logout().subscribe({
      next: () => { 
        this.popupService.openDialog(MessagePopupComponent, {
          data: { message: 'You have logged out of your session.' }
        });
        this.router.navigate(['/home']);
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Logout failed', error);
        this.cdr.detectChanges();
      }
    });
    this.closeMobileMenu();
  }

  @HostListener('document:click', ['$event']) closeMenus(event: any) {
    const target = event.target as HTMLElement;
    
    // Fermer le menu mobile si on clique en dehors
    if(!target.closest('.mobile-menu-container') && !target.closest('.mobile-menu-button')) {
      this.isMobileMenuOpen = false;
      this.isUserSubMenuOpen = false;
    }

  }

  @HostListener('document:keydown.escape', ['$event']) closeMenusOnEscape(event: any) {
    this.isMobileMenuOpen = false;
    this.isUserSubMenuOpen = false;
  }

  isAuthenticated() {
    return this.userDetails != null && Object.keys(this.userDetails).length > 0;
  }

  ngOnDestroy() {
    this.idleSubscriptions.forEach(sub => sub.unsubscribe());
    this.idle.stop();
  }

  showHappiFeedback = signal(false);
  private pollStorageForLanguage(retriesLeft: number): void {
    if(retriesLeft <= 0) {
      return;
    }

    setTimeout(() => {
      const appQueId = this.cacheService.getItem('appQueId');
      
      if(appQueId && typeof appQueId === 'string' && appQueId.trim() !== '') {
        this.showHappiFeedback.set(true);
        console.log('appQueId found in storage:', appQueId);
      } else {
        this.pollStorageForLanguage(retriesLeft - 1);
      }
    }, 1000);
  }
}